import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, of, Subscription } from 'rxjs';
import { getControl } from 'src/app/_helpers/utils';
import { RcCompanyMetadata } from 'src/app/_types/lead.types';
import {
  BusinessRcCompanyCoverageValidity,
  getBusinessRcCompanyCoverageValidityLabel,
} from 'src/app/enums/business/business-rc-company-coverage-validity.enum';
import {
  getRcCompanyAdditionalCoverLabel,
  RcCompanyAdditionalCover,
} from 'src/app/enums/business/rc-company-additional-cover.enum';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';

import { LeadFormBase } from '../../lead-form-base';
import { SharedBusinessFormComponent } from '../shared-business-form/shared-business-form.component';

type BusinessRcCompanyGenericCoverageFormGroup = {
  withCoverage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  deductible: FormControl<number | null>;
};

type CompanyPersonalDamageCoverageFormGroup = {
  personalPropertyDamage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  doubleWarranty: FormControl<boolean | null>;
  dcFranchise: FormControl<number | null>;
  dmFranchise: FormControl<number | null>;
  financialLoss: FormControl<boolean | null>;
  financialLossInsuredSum: FormControl<number | null>;
  financialLossDeductible: FormControl<number | null>;
  financialLossDataViolationCoverage: FormControl<boolean | null>;
};

@Component({
  selector: 'app-rc-company-form',
  templateUrl: './rc-company-form.component.html',
  styleUrls: ['./rc-company-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
    SharedBusinessFormComponent,
  ],
})
export class RcCompanyFormComponent extends LeadFormBase implements OnDestroy {
  @Input()
  formGroup!: FormGroup;

  private subscriptions: Subscription[] = [];

  constructor(private fb: FormBuilder) {
    super();
  }

  sharedBusinessFormGroup: FormGroup = this.fb.group({
    activityArea: [null],
    activityDescription: [null],
    hasLaborAgreement: [null],
    laborAgreement: [null],
    isInternational: [null],
    activityCountries: [null],
    hasSubcontractors: [null],
    collaboratorsCount: [null],
    yearlyAnnualRevenue: [null],
    yearlySalaries: [null],
  });

  companyPersonalDamageCoverageFormGroup: FormGroup<CompanyPersonalDamageCoverageFormGroup> =
    this.fb.group({
      personalPropertyDamage: [null],
      insuredSum: [null],
      doubleWarranty: [null],
      dcFranchise: [null],
      dmFranchise: [null],
      financialLoss: [null],
      financialLossInsuredSum: [null],
      financialLossDeductible: [null],
      financialLossDataViolationCoverage: [null],
    });

  createGenericCoverageFormGroup(): FormGroup<BusinessRcCompanyGenericCoverageFormGroup> {
    return this.fb.group({
      withCoverage: [null],
      insuredSum: [null],
      deductible: [null],
    });
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_RC_COMPANY,
    reimbursedSum: [null],
    insuredSum: [null],
    additionalCovers: [null],
    hasInsurance: [null],
    currentInsurer: [null],
    hasClaimsLastFiveYears: [null],
    fees: [null],
    coverageValidityDuration: [null],
    companyPersonalDamageCoverage: this.companyPersonalDamageCoverageFormGroup,
    environmentDamageBasicCoverage: this.createGenericCoverageFormGroup(),
    environmentDamageExtendedCoverage: this.createGenericCoverageFormGroup(),
    damagePreventionCoverage: this.createGenericCoverageFormGroup(),
    misappropriationDamageCoverage: this.createGenericCoverageFormGroup(),
    reputationRestorationCoverage: this.createGenericCoverageFormGroup(),
    structureDamagesCoverage: this.createGenericCoverageFormGroup(),
    importerInsured: [null],
    intangibleAssets: [null],
    eanBarcodes: [null],
    ionizingRadiation: [null],
    cableTransportInstallationCombination: [null],
    firefightingExplosiveMaterialShipping: [null],
    buildingCoverage: this.createGenericCoverageFormGroup(),
    itemsCoverage: this.createGenericCoverageFormGroup(),
    cisternCoverage: this.createGenericCoverageFormGroup(),
    installationAssemblyCoverage: this.createGenericCoverageFormGroup(),
    workingCommunitiesCoverage: this.createGenericCoverageFormGroup(),
    workedObjectBasicCoverage: this.createGenericCoverageFormGroup(),
    workedObjectExtendedCoverage: this.createGenericCoverageFormGroup(),
    sharedBusiness: this.sharedBusinessFormGroup,
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const businessMetadata = this.metadata as RcCompanyMetadata;

      // Patch shared business data
      if (businessMetadata.activityArea !== undefined) {
        this.sharedBusinessFormGroup.patchValue({
          activityArea: businessMetadata.activityArea,
          activityDescription: businessMetadata.activityDescription,
          hasLaborAgreement: businessMetadata.hasLaborAgreement,
          laborAgreement: businessMetadata.laborAgreement,
          isInternational: businessMetadata.isInternational,
          activityCountries: businessMetadata.activityCountries,
          hasSubcontractors: businessMetadata.hasSubcontractors,
          collaboratorsCount: businessMetadata.collaboratorsCount,
          yearlyAnnualRevenue: businessMetadata.yearlyAnnualRevenue,
          yearlySalaries: businessMetadata.yearlySalaries,
        });
      }

      // Patch company personal damage coverage
      if (businessMetadata.companyPersonalDamageCoverage) {
        this.companyPersonalDamageCoverageFormGroup.patchValue(
          businessMetadata.companyPersonalDamageCoverage
        );
      }

      // Patch generic coverage groups
      const genericCoverageGroups = [
        'environmentDamageBasicCoverage',
        'environmentDamageExtendedCoverage',
        'damagePreventionCoverage',
        'misappropriationDamageCoverage',
        'reputationRestorationCoverage',
        'structureDamagesCoverage',
        'buildingCoverage',
        'itemsCoverage',
        'cisternCoverage',
        'installationAssemblyCoverage',
        'workingCommunitiesCoverage',
        'workedObjectBasicCoverage',
        'workedObjectExtendedCoverage',
      ] as const;

      genericCoverageGroups.forEach((groupName) => {
        if (businessMetadata[groupName]) {
          (this.metadataFormGroup.controls[groupName] as FormGroup).patchValue(
            businessMetadata[groupName]
          );
        }
      });

      // Patch remaining top-level fields
      this.metadataFormGroup.patchValue({
        type: businessMetadata.type,
        reimbursedSum: businessMetadata.reimbursedSum,
        insuredSum: businessMetadata.insuredSum,
        additionalCovers: businessMetadata.additionalCovers,
        hasInsurance: businessMetadata.hasInsurance,
        currentInsurer: businessMetadata.currentInsurer,
        hasClaimsLastFiveYears: businessMetadata.hasClaimsLastFiveYears,
        fees: businessMetadata.fees,
        coverageValidityDuration: businessMetadata.coverageValidityDuration,
        importerInsured: businessMetadata.importerInsured,
        intangibleAssets: businessMetadata.intangibleAssets,
        eanBarcodes: businessMetadata.eanBarcodes,
        ionizingRadiation: businessMetadata.ionizingRadiation,
        cableTransportInstallationCombination:
          businessMetadata.cableTransportInstallationCombination,
        firefightingExplosiveMaterialShipping:
          businessMetadata.firefightingExplosiveMaterialShipping,
      });
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    // Handle conditional fields
    this.setupConditionalFieldSubscriptions();
  }

  private setupConditionalFieldSubscriptions(): void {
    const hasInsuranceSub = this.metadataFormGroup.controls[
      'hasInsurance'
    ].valueChanges.subscribe((value: boolean | string | null) => {
      if (value !== true && value !== 'true') {
        this.metadataFormGroup.controls['currentInsurer'].reset();
      }
    });
    this.subscriptions.push(hasInsuranceSub);

    // Handle financialLoss -> financial loss related fields
    const financialLossSub =
      this.companyPersonalDamageCoverageFormGroup.controls[
        'financialLoss'
      ].valueChanges.subscribe((value: boolean | string | null) => {
        if (value !== true && value !== 'true') {
          this.companyPersonalDamageCoverageFormGroup.controls[
            'financialLossInsuredSum'
          ].reset();
          this.companyPersonalDamageCoverageFormGroup.controls[
            'financialLossDeductible'
          ].reset();
          this.companyPersonalDamageCoverageFormGroup.controls[
            'financialLossDataViolationCoverage'
          ].reset();
        }
      });
    this.subscriptions.push(financialLossSub);

    // Handle all generic coverage groups withCoverage -> insuredSum and deductible
    const genericCoverageGroups = [
      'environmentDamageBasicCoverage',
      'environmentDamageExtendedCoverage',
      'damagePreventionCoverage',
      'misappropriationDamageCoverage',
      'reputationRestorationCoverage',
      'structureDamagesCoverage',
      'buildingCoverage',
      'itemsCoverage',
      'cisternCoverage',
      'installationAssemblyCoverage',
      'workingCommunitiesCoverage',
      'workedObjectBasicCoverage',
      'workedObjectExtendedCoverage',
    ] as const;

    genericCoverageGroups.forEach((groupName) => {
      const coverageGroup = this.metadataFormGroup.controls[
        groupName
      ] as FormGroup;
      const withCoverageControl = coverageGroup.controls['withCoverage'];

      const coverageSub = withCoverageControl.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            coverageGroup.controls['insuredSum'].reset();
            coverageGroup.controls['deductible'].reset();
          }
        }
      );
      this.subscriptions.push(coverageSub);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  // Enum values and labels
  additionalCoverValues = Object.values(RcCompanyAdditionalCover);

  getRcCompanyAdditionalCoverLabel = getRcCompanyAdditionalCoverLabel;

  coverageValidityValues = Object.values(BusinessRcCompanyCoverageValidity);

  getBusinessRcCompanyCoverageValidityLabel =
    getBusinessRcCompanyCoverageValidityLabel;

  getControl(control: AbstractControl): FormControl {
    return getControl(control);
  }

  override handleSubmit(): Observable<boolean> {
    // Merge shared business form data into the main metadata form
    const sharedData = this.sharedBusinessFormGroup.value;
    Object.keys(sharedData).forEach((key) => {
      this.metadataFormGroup.controls[key] =
        this.sharedBusinessFormGroup.controls[key];
    });
    this.metadataFormGroup.removeControl('sharedBusiness');
    return of(true);
  }
}
